<template>
  <div class="grid-view">
    <div class="main-content financial-exchange-rate">
      <div class="section operations">
        <div class="filters">
          <el-form :inline="true" label-width="90px" label-position="left" @submit.native.prevent>
            <el-form-item>
              <el-input-number
                v-if="showMonthSelector"
                v-model="currentMonth"
                controls-position="right"
                :precision="0"
                @change="handleMonthChange"
                @keyup.enter.native="handleMonthChange"
              />
            </el-form-item>
            <span v-if="!isLock">
              <el-button type="primary" @click="saveHandler">{{ $t('common.btn.save') }}</el-button>
              <el-button type="primary" @click="cancelHandler">{{ $t('common.btn.cancel') }}</el-button>
            </span>
            <span v-else>
              <el-button v-if="$checkPermission(['button_FinancialExchangeRateList_add'])" :disabled="!(showMonthSelector || fiscalPeriod)" type="primary" @click="addHandler">{{ $t('common.btn.new') }}</el-button>
              <el-button v-if="$checkPermission(['button_FinancialExchangeRateList_edit'])" type="primary" :disabled="!selectedRow" @click="editHandler">{{ $t('common.btn.edit') }}</el-button>
              <el-button v-if="$checkPermission(['button_FinancialExchangeRateList_delete'])" type="danger" :disabled="!selectedRow" @click="deleteHandler">{{ $t('common.btn.delete') }}</el-button>
              <el-button type="primary" @click="refreshHandler">{{ $t('common.btn.refresh') }}</el-button>
            </span>
          </el-form>
        </div>
      </div>
      <div class="section grid-wrapper">
        <cargo-ag-grid
          :show-side-bar="false"
          :grid-params.sync="gridParams"
          :grid-options="gridOptions"
          :column-defs="columnDefs"
          :row-data="rowData"
          style="width: 100%; height: 100%"
        />
      </div>
    </div>
  </div>
</template>

<script>

import DateUtils from '@/utils/dateUtils'
import ColumnUtils from '@/utils/ColumnUtils'
import AdvancedSearchOption from '@/class/cargo/model/parameters/AdvancedSearchOption'
import { getLoading } from '@/utils/utilBaseElement'
import * as financialExchangeRate from '@/api/financeManage/financialExchangeRate'
import * as RateUtil from '@/class/cargo/model/utils/RateUtil'
import CargoAgGridCommonInputEditor from '@/components/Cargo/Common/CargoAgGridCommonInputEditor/index'
import { CurrencyUtil } from '@/class/cargo/model/utils/CurrencyUtil'

export default {
  name: 'FinancialExchangeRateList',
  components: { },
  props: {
    accountSet: { type: Object, default: null },
    accountSets: { type: Array, default: null },
    showPeriod: { type: Boolean, default: false },
    showMonthSelector: { type: Boolean, default: true }
  },
  data() {
    return {
      selectedRow: null, // 是否选中行
      currentState: 'read', // read|edit|add
      currentMonth: '',
      gridParams: {},
      gridOptions: {
        rowSelection: 'single',
        suppressCellSelection: true,
        defaultColDef: {
          resizable: true,
          filter: true
        },
        pagination: false,
        singleClickEdit: true,
        frameworkComponents: {
          CargoAgGridCommonInputEditor
        },
        onRowClicked: this.onRowCLick
      },
      rowData: [],
      showAccountSet: false,
      fiscalPeriod: null
    }
  },
  computed: {
    columnDefs() {
      return [
        {
          headerName: this.$t('CargoMsg.Cargo_SetsOfBooks'),
          field: 'accountSetUuid',
          width: 90,
          hide: !this.showAccountSet,
          // 显示数据格式化
          valueFormatter: params => {
            return this.accountSetLabelFun(params.value)
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_Periods'),
          field: 'year',
          colId: 'year1',
          width: 90,
          hide: !this.showPeriod
        }, {
          headerName: this.$t('CargoMsg.Cargo_Year'),
          field: 'year',
          colId: 'year2',
          width: 90,
          hide: this.showPeriod,
          editable: !this.isLock,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              precision: 0,
              min: 2000,
              max: 2099,
              controlsPosition: 'right',
              style: 'width: 100%'
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_Month'),
          field: 'month',
          width: 90,
          hide: this.showPeriod,
          editable: !this.isLock,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              precision: 0,
              min: 1,
              max: 12,
              controlsPosition: 'right',
              style: 'width: 100%'
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_OriginalCurrency'),
          field: 'sourceCurrency',
          width: 90,
          editable: !this.isLock,
          cellEditor: 'CargoAgGridCommonInputEditor',
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_BaseCurrency'),
          field: 'targetCurrency',
          width: 150,
          editable: !this.isLock,
          cellEditor: 'CargoAgGridCommonInputEditor',
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_ExchangeRate'),
          field: 'rate',
          width: 120,
          editable: !this.isLock,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              min: 0,
              max: 99999,
              controlsPosition: 'right',
              style: 'width: 100%'
            }
          },
          valueFormatter: params => {
            return ColumnUtils.numberColumnLabelFunc(params.value, 8, true)
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        }
      ]
    },
    isLock() {
      return this.currentState === 'read'
    }
  },
  watch: {
    fiscalPeriod() {
      if (this.currentState === 'read') {
        this.loadRates()
      }
    }
  },
  mounted() {
    this.currentMonth = DateUtils.customFormat(new Date(), 'YYYYMM')
    if (this.showMonthSelector) {
      this.loadRates()
    }
  },
  methods: {
    handleChange() {
      this.loadRates()
    },
    handleNativeChange() {
    },
    onRowCLick(event) {
      this.selectedRow = event.data
    },
    accountSetLabelFun(value) {
      if (value) {
        const accountSet = this.accountSets.find(accountSet => accountSet.uuid === value)
        return accountSet ? accountSet.name : this.$t('CargoMsg.Cargo_Temp')
      }
      return this.$t('CargoMsg.Cargo_Temp')
    },
    // 加载汇率
    async loadRates() {
      const conditions = []
      const currentMonth = String(this.currentMonth)
      const year = this.fiscalPeriod == null ? Number(currentMonth.substr(0, 4)) : this.fiscalPeriod.year
      const month = this.fiscalPeriod == null ? Number(currentMonth.substr(4)) : this.fiscalPeriod.month
      conditions.push(new AdvancedSearchOption(null, 'YEAR', year))
      conditions.push(new AdvancedSearchOption(null, 'MONTH', month))
      // todo 确认是否还需要使用此判断
      // if ((this.fiscalPeriod && this.fiscalPeriod.useGroupExchangeRate) || !this.fiscalPeriod) {
      //   conditions.push(new AdvancedSearchOption(null, 'ACCOUNT_SET_UUID', null, AdvancedSearchConsts.OPERATOR_IS_NULL.code, null))
      // } else
      if (this.accountSet && this.accountSet.uuid) {
        conditions.push(new AdvancedSearchOption(null, 'ACCOUNT_SET_UUID', this.accountSet.uuid))
      } else {
        conditions.push(new AdvancedSearchOption(null, 'ACCOUNT_SET_UUID', ''))
      }
      this.rowData = []
      const loading = getLoading()
      financialExchangeRate.loadByAccountSet(conditions).then(resp => {
        this.rowData = resp.data
        if (this.rowData === null) {
          this.rowData = []
        }
      }).finally(() => loading.close())
      this.gridOptions.api.clearFocusedCell()
      this.selectedRow = null
    },
    addHandler() {
      if (this.rowData === null) {
        return
      }
      const loading = getLoading()
      financialExchangeRate.initNewRates().then(resp => {
        this.initResultHandler(resp)
      }).finally(() => loading.close())
    },
    initResultHandler(resp) {
      this.currentState = 'edit'
      const rates = resp.data
      if (rates && rates.length > 0) {
        const currentMonth = String(this.currentMonth)
        const year = this.fiscalPeriod == null ? Number(currentMonth.substr(0, 4)) : this.fiscalPeriod.year
        const month = this.fiscalPeriod == null ? Number(currentMonth.substr(4)) : this.fiscalPeriod.month
        for (const rate of rates) {
          rate.year = year
          rate.month = month
          rate.rate = RateUtil.getForeignExchangeRateSafely(rate.targetCurrency, rate.sourceCurrency, new Date(year, month, 1), 0)
          if (this.accountSet && this.accountSet.uuid) {
            rate.accountSetUuid = this.accountSet.uuid
          }
        }
        this.rowData.push(...rates)
      }
    },
    editHandler() {
      if (this.rowData.length > 0) {
        this.currentState = 'edit'
      }
    },
    async deleteHandler() {
      const cell = this.gridOptions.api.getFocusedCell()
      // const rate = cell ? this.rowData[cell.rowIndex] : null
      const rate = cell ? this.selectedRow : null
      if (rate) {
        await this.$confirm(this.$t('CargoMsg.Cargo_SureToDeleteTheExchangeRate'), this.$t('common.tips.confirm'), {
          confirmButtonText: this.$t('common.btn.ok'),
          cancelButtonText: this.$t('common.btn.cancel'),
          canCloseModule: true,
          type: 'warning'
        })
        const loading = getLoading()
        const rateUuid = rate.uuid
        await financialExchangeRate.remove({ rateUuid }).then(resp => {
          this.removeSuccess(rateUuid)
          // this.$message.success(this.$t('CargoMsg.Cargo_DeletedSuccessfully'))
        }).finally(() => loading.close())
      }
    },
    refreshHandler() {
      this.loadRates()
    },
    saveHandler() {
      const saveList = []
      for (const rate of this.rowData) {
        if (rate.dirty || !rate.uuid) {
          saveList.push(rate)
        }
      }
      if (saveList.length) {
        const loading = getLoading()
        financialExchangeRate.saveAll(saveList).then(resp => {
          this.saveSuccess(resp)
          this.$message.success(this.$t('CargoMsg.Cargo_SaveSucceed'))
        }).finally(() => loading.close())
      } else {
        this.$message.warning(this.$t('CargoMsg.Cargo_NoDataModifiedNoNeedToSave'))
      }
      this.currentState = 'read'
    },
    async cancelHandler() {
      const loading = getLoading()
      await this.loadRates()
      loading.close()
      this.currentState = 'read'
    },
    onCellChange(params) {
      const { node, data } = params
      if (params.colDef.field === 'rate') {
        const rate = CurrencyUtil.truncateRateByCurrency(data.sourceCurrency, data.rate)
        if (rate !== data.rate) {
          node.setDataValue('rate', rate)
          return
        }
      }
      if (node.data.uuid) {
        node.data.dirty = true
      }
    },
    saveSuccess(resp) {
      // 返回的列表中可能同时含有编辑的和新增的数据
      const list = resp.data
      if (!list || list.length === 0) return
      // 1 遍历rowData列表能匹配到uuid的数据（编辑的数据），更新数据
      for (let i = 0; i < this.rowData.length; i++) {
        const row = this.rowData[i]
        if (row.uuid) {
          const index = list.findIndex(params => params.uuid === row.uuid)
          if (index > -1) {
            Object.assign(row, list[index])
            list.splice(index, 1)
          }
        }
      }
      // 2 遍历rowData列表中没有uuid的数据 删除该数据
      for (let i = this.rowData.length - 1; i >= 0; i--) {
        if (!this.rowData[i].uuid) {
          this.rowData.splice(i, 1)
        }
      }
      // 3 添加列表里没有匹配uuid的数据（新增的数据）到列表
      this.rowData.push(...list)
      this.gridOptions.api.refreshCells({ force: true })
    },
    removeSuccess(uuid) {
      const index = this.rowData.findIndex(params => params.uuid === uuid)
      if (index > -1) {
        this.rowData.splice(index, 1)
        this.gridOptions.api.refreshCells({ force: true })
      }
      this.$message.success(this.$t('Message.DeleteSuccessfully'))
    },
    handleMonthChange(value) {
      value = value + ''
      this.currentMonth = DateUtils.generationMonth(value)
      this.loadRates()
    }
  }
}
</script>

<style scoped>

</style>
